import {Avatar, Card, Icon, Tag, Tooltip, Typography} from 'antd';
import React from 'react';
import {FlightLeg} from '../../../schema';
import PositioningLegTag from './positioning-leg-tag';
import './flight-route-display.less';

interface FlightRouteCardProps {
    title: React.ReactNode;
    legCount: number;
    className?: string;
    headerExtra?: React.ReactNode;
    compactHeader?: boolean;
    showTitleIcon?: boolean;
    children: React.ReactNode;
}

export function FlightRouteCard(props: FlightRouteCardProps) {
    const {title, legCount, className = '', headerExtra, compactHeader = false, showTitleIcon = true, children} = props;
    const cardClassNames = [
        'flight-route-card',
        compactHeader ? 'flight-route-card--compact-header' : '',
        className
    ].filter(Boolean).join(' ');

    return (
        <Card
            size="small"
            className={cardClassNames}
            title={<span>{showTitleIcon ? <><Icon type="environment"/> </> : null}{title} ({legCount})</span>}
            extra={headerExtra ? (
                <div className="flight-route-card__header-actions">{headerExtra}</div>
            ) : undefined}
        >
            {children}
        </Card>
    );
}

interface FlightRouteLegContentProps {
    leg: FlightLeg;
    index: number;
    totalLegs: number;
    compact?: boolean;
    status?: React.ReactNode;
}

/**
 * Canonical visual representation of one flight leg. The preview and both
 * assignment modes provide their own interaction shell, while sharing route,
 * load, marker, and positioning presentation here.
 */
export function FlightRouteLegContent(props: FlightRouteLegContentProps) {
    const {leg, index, totalLegs, compact = false, status} = props;
    const paxCount = Number(leg.paxCount || 0);
    const cgoCount = Number(leg.cgoCount || 0);
    const bagCount = Number(leg.bagCount || 0);
    const loadWeight = Number(leg.paxWeight || 0) +
        Number(leg.bagWeight || 0) +
        Number(leg.cgoWeight || 0);
    const positioning = paxCount + cgoCount === 0;
    const departure = leg.departure || leg.departureID;
    const destination = leg.destination || leg.destinationID;
    const classNames = [
        'flight-route-leg',
        compact ? 'flight-route-leg--compact' : '',
        index < totalLegs - 1 ? 'flight-route-leg--connected' : ''
    ].filter(Boolean).join(' ');

    return (
        <div className={classNames}>
            <span className="flight-route-leg__marker">
                <Avatar
                    size="small"
                    className={positioning
                        ? 'flight-route-leg__avatar flight-route-leg__avatar--positioning'
                        : 'flight-route-leg__avatar'}
                >
                    {index + 1}
                </Avatar>
            </span>

            <div className="flight-route-leg__content">
                <div className="flight-route-leg__route-line">
                    <div className="flight-route-leg__locations">
                        <Tooltip title={departure} mouseEnterDelay={0.5}>
                            <Typography.Text strong className="flight-route-leg__location">
                                {departure}
                            </Typography.Text>
                        </Tooltip>
                        <Icon type="arrow-right" className="flight-route-leg__direction"/>
                        <Tooltip title={destination} mouseEnterDelay={0.5}>
                            <Typography.Text strong className="flight-route-leg__location">
                                {destination}
                            </Typography.Text>
                        </Tooltip>
                    </div>
                    {status ? <div className="flight-route-leg__status">{status}</div> : null}
                </div>

                <div className="flight-route-leg__metrics">
                    <Tag color="geekblue" aria-label={`${paxCount} PAX`}>
                        <Icon type="user"/> {paxCount}<span className="flight-route-leg__metric-label"> PAX</span>
                    </Tag>
                    <Tag aria-label={`${bagCount} ${bagCount === 1 ? 'bag' : 'bags'}`}>
                        <Icon type="shopping"/> {bagCount}
                        <span className="flight-route-leg__metric-label"> {bagCount === 1 ? 'bag' : 'bags'}</span>
                    </Tag>
                    <Tag color="purple" aria-label={`${cgoCount} CGO`}>
                        <Icon type="inbox"/> {cgoCount}<span className="flight-route-leg__metric-label"> CGO</span>
                    </Tag>
                    <Tag aria-label={`${loadWeight} pounds load`}>
                        <Icon type="dashboard"/> {loadWeight}<span className="flight-route-leg__metric-label"> lb</span>
                    </Tag>
                    {positioning ? <PositioningLegTag/> : null}
                </div>
            </div>
        </div>
    );
}
